/**
 * Единый реестр обработанных waitingListId: metadata, звук, баннер.
 */

import { VCS_DISMISSED_WAITING_IDS_KEY } from 'consts';

type HandledFlags = {
    hideFromMetadata: boolean;
    suppressSound: boolean;
    dismissBanner: boolean;
};

const handled = new Map<string, HandledFlags>();

const loadDismissedBannerFromSession = (): void => {
    try {
        const raw = sessionStorage.getItem(VCS_DISMISSED_WAITING_IDS_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return;
        parsed.forEach((id: string) => {
            const prev = handled.get(id) ?? { hideFromMetadata: false, suppressSound: false, dismissBanner: false };
            handled.set(id, { ...prev, dismissBanner: true });
        });
    } catch {
        // ignore
    }
};

loadDismissedBannerFromSession();

const persistDismissedBanner = (): void => {
    const ids = [...handled.entries()].filter(([, f]) => f.dismissBanner).map(([id]) => id);
    try {
        sessionStorage.setItem(VCS_DISMISSED_WAITING_IDS_KEY, JSON.stringify(ids));
    } catch {
        // ignore
    }
};

export type MarkWaitingHandledOptions = {
    hideFromMetadata?: boolean;
    suppressSound?: boolean;
    dismissBanner?: boolean;
};

export const markWaitingListHandled = (ids: string[], options: MarkWaitingHandledOptions = {}): void => {
    const { hideFromMetadata = true, suppressSound = true, dismissBanner = false } = options;

    ids.forEach((id) => {
        const prev = handled.get(id) ?? { hideFromMetadata: false, suppressSound: false, dismissBanner: false };
        handled.set(id, {
            hideFromMetadata: prev.hideFromMetadata || hideFromMetadata,
            suppressSound: prev.suppressSound || suppressSound,
            dismissBanner: prev.dismissBanner || dismissBanner,
        });
    });

    if (dismissBanner) {
        persistDismissedBanner();
    }
};

export const dismissBannerForWaitingIds = (ids: string[]): void => {
    markWaitingListHandled(ids, {
        hideFromMetadata: false,
        suppressSound: true,
        dismissBanner: true,
    });
};

export const isWaitingListHiddenFromMetadata = (waitingListId: string): boolean =>
    handled.get(waitingListId)?.hideFromMetadata ?? false;

export const isWaitingRequestSoundSuppressed = (waitingListId: string): boolean =>
    handled.get(waitingListId)?.suppressSound ?? false;

export const isWaitingBannerDismissed = (waitingListId: string): boolean =>
    handled.get(waitingListId)?.dismissBanner ?? false;

export const clearWaitingListRegistry = (): void => {
    handled.clear();
    sessionStorage.removeItem(VCS_DISMISSED_WAITING_IDS_KEY);
};

export const applyWaitingUsersFromServer = <T extends { waitingListId: string }>(incoming: T[] | undefined): T[] => {
    const list = incoming ?? [];
    return list.filter((user) => !isWaitingListHiddenFromMetadata(user.waitingListId));
};
